/**
 * MEDalign product page — custom elements.
 *
 * Everything here is a custom element, so Theme Editor section reloads,
 * duplications and removals are handled by the browser lifecycle:
 *   connectedCallback    -> initialise (runs again on every re-render)
 *   disconnectedCallback -> tear listeners down (no leaks, no double-binding)
 * Multiple instances of the same section never collide because all state is
 * held on the element instance, never on window or a module-level singleton.
 */

/* -------------------------------------------------------------------------
   Product media gallery: scroll-snap track + thumbnails + arrows + zoom
   ------------------------------------------------------------------------- */
class MedalignGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-md-track]');
    this.thumbs = Array.from(this.querySelectorAll('[data-md-thumb]'));
    this.slides = Array.from(this.querySelectorAll('[data-md-slide]'));
    this.prevBtn = this.querySelector('[data-md-prev]');
    this.nextBtn = this.querySelector('[data-md-next]');
    this.index = 0;
    this.controller = new AbortController();
    const { signal } = this.controller;

    if (!this.track) return;

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i), { signal });
    });

    this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
    this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1), { signal });

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') { this.goTo(this.index + 1); }
        else if (event.key === 'ArrowLeft') { this.goTo(this.index - 1); }
      },
      { signal }
    );

    // zoom
    this.querySelectorAll('[data-md-zoom]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const dialogId = this.getAttribute('data-lightbox');
          const dialog = dialogId ? document.getElementById(dialogId) : null;
          if (dialog && typeof dialog.openWith === 'function') dialog.openWith(button.dataset.mdZoom, button.dataset.mdZoomAlt || '');
        },
        { signal }
      );
    });

    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    cancelAnimationFrame(this.raf);
  }

  goTo(index) {
    if (!this.track || !this.slides.length) return;
    const next = Math.max(0, Math.min(this.slides.length - 1, index));
    this.index = next;
    this.track.scrollTo({
      left: next * this.track.clientWidth,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    this.sync();
  }

  onScroll() {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => {
      if (!this.track.clientWidth) return;
      const next = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (next !== this.index) {
        this.index = next;
        this.sync();
      }
    });
  }

  sync() {
    this.thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });
    if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index >= this.slides.length - 1;
  }
}

/* -------------------------------------------------------------------------
   Modal dialog (size guide, image zoom). Native <dialog> gives us focus
   trapping, Escape-to-close and inert background for free.
   ------------------------------------------------------------------------- */
class MedalignDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.image = this.querySelector('[data-md-dialog-image]');
    this.controller = new AbortController();
    const { signal } = this.controller;
    if (!this.dialog) return;

    this.querySelectorAll('[data-md-dialog-close]').forEach((button) => {
      button.addEventListener('click', () => this.close(), { signal });
    });

    // click on the backdrop area closes
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog || event.target.hasAttribute('data-md-dialog-overlay')) this.close();
      },
      { signal }
    );

    // buttons anywhere on the page that open this dialog
    this.openers = Array.from(document.querySelectorAll(`[data-md-dialog-open="${this.id}"]`));
    this.openers.forEach((button) => {
      button.addEventListener('click', () => this.open(), { signal });
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.dialog?.open) this.dialog.close();
  }

  open() {
    if (!this.dialog?.open) this.dialog.showModal();
  }

  openWith(src, alt) {
    if (this.image && src) {
      this.image.src = src;
      this.image.alt = alt || '';
    }
    this.open();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }
}

/* -------------------------------------------------------------------------
   Mobile sticky add-to-cart bar.
   It never owns cart logic — it forwards the click to the real product form
   so Whisper's product-form-component handles the AJAX add and cart drawer.
   ------------------------------------------------------------------------- */
class MedalignStickyAtc extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-md-sticky-bar]');
    this.button = this.querySelector('[data-md-sticky-add]');
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.button?.addEventListener(
      'click',
      () => {
        const form = this.targetForm();
        if (!form) return;
        const submit = form.querySelector('button[name="add"]:not([disabled])');
        if (submit) submit.click();
        else form.requestSubmit?.();
      },
      { signal }
    );

    const anchorId = this.getAttribute('data-observe');
    this.anchor = anchorId ? document.getElementById(anchorId) : null;

    if (this.anchor && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          // show the bar once the main buy button has scrolled out of view
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.bar?.setAttribute('data-visible', show ? 'true' : 'false');
        },
        { rootMargin: '0px 0px -20% 0px' }
      );
      this.observer.observe(this.anchor);
    } else {
      this.bar?.setAttribute('data-visible', 'true');
    }

    // In the Theme Editor, always show the bar while this section is selected
    // so the merchant can actually see what they are editing.
    if (window.Shopify?.designMode) {
      document.addEventListener(
        'shopify:section:select',
        (event) => {
          if (event.target.contains(this)) this.bar?.setAttribute('data-visible', 'true');
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  targetForm() {
    const id = this.getAttribute('data-form');
    return (id && document.getElementById(id)) || document.querySelector('form[data-type="add-to-cart-form"]');
  }
}

/* -------------------------------------------------------------------------
   Theme Editor block selection: scroll a horizontal carousel to the block
   the merchant just clicked in the sidebar. Registered once per element.
   ------------------------------------------------------------------------- */
class MedalignScroller extends HTMLElement {
  connectedCallback() {
    if (!window.Shopify?.designMode) return;
    this.controller = new AbortController();
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!this.contains(event.target)) return;
        event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      },
      { signal: this.controller.signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get('medalign-gallery')) customElements.define('medalign-gallery', MedalignGallery);
if (!customElements.get('medalign-dialog')) customElements.define('medalign-dialog', MedalignDialog);
if (!customElements.get('medalign-sticky-atc')) customElements.define('medalign-sticky-atc', MedalignStickyAtc);
if (!customElements.get('medalign-scroller')) customElements.define('medalign-scroller', MedalignScroller);
